import { getAccessToken } from "../utils/auth";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import axios from "../utils/api.js";
import {
  Typography,
  Grid,
  Box,
  IconButton,
  Chip,
  Skeleton,
  Avatar,
  Snackbar,
  Alert,
  Tooltip,
  Collapse,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { useNavigate, useParams } from "react-router-dom";
import AccessDenied from "../components/AccessDenied";
import { keyframes } from "@emotion/react";

const accent = "#0d47a1";
const accentLight = "rgba(13, 71, 161, 0.08)";
const accentGradient = "linear-gradient(135deg, #0d47a1 0%, #1565c0 50%, #1976d2 100%)";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const STATUS_STYLES = {
  captured: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
  completed: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
  active: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
  activated: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
  charged: { bg: "#dcfce7", color: "#15803d", border: "#86efac" },
  authenticated: { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" },
  authorized: { bg: "#dbeafe", color: "#1d4ed8", border: "#93c5fd" },
  pending: { bg: "#fef3c7", color: "#b45309", border: "#fcd34d" },
  refunded: { bg: "#ffedd5", color: "#c2410c", border: "#fdba74" },
  halted: { bg: "#fef3c7", color: "#b45309", border: "#fcd34d" },
  failed: { bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5" },
  cancelled: { bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5" },
  created: { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" },
};

const SUCCESS_STATUSES = new Set([
  "captured",
  "completed",
  "active",
  "activated",
  "charged",
]);

const decodeEmail = (encodedEmail) => {
  try {
    return atob(decodeURIComponent(encodedEmail));
  } catch {
    return encodedEmail;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatCurrency = (amount, currency = "INR") => {
  const value = Number(amount) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
  }).format(value);
};

const getInitials = (name, email) => {
  if (name?.trim()) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  const local = (email || "").split("@")[0] || "";
  return local.slice(0, 2).toUpperCase() || "?";
};

const getStatusStyle = (status) =>
  STATUS_STYLES[status] || STATUS_STYLES.created;

const CopyableValue = ({ value, onCopy }) => {
  if (!value) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{
          color: "#334155",
          fontFamily: "ui-monospace, monospace",
          fontSize: "0.8rem",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </Typography>
      <Tooltip title="Copy">
        <IconButton
          size="small"
          onClick={() => onCopy(value)}
          sx={{
            flexShrink: 0,
            color: "#94a3b8",
            "&:hover": { color: accent, bgcolor: accentLight },
          }}
        >
          <ContentCopyRoundedIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const DetailRow = ({ label, value, mono, onCopy }) => (
  <Box sx={{ py: 1.25, borderBottom: "1px solid #f1f5f9", "&:last-child": { borderBottom: 0 } }}>
    <Typography
      variant="caption"
      sx={{
        color: "#94a3b8",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        fontSize: "0.65rem",
        display: "block",
        mb: 0.35,
      }}
    >
      {label}
    </Typography>
    {onCopy ? (
      <CopyableValue value={value} onCopy={onCopy} />
    ) : (
      <Typography
        variant="body2"
        sx={{
          color: "#334155",
          fontWeight: 500,
          ...(mono && {
            fontFamily: "ui-monospace, monospace",
            fontSize: "0.8rem",
          }),
        }}
      >
        {value ?? "—"}
      </Typography>
    )}
  </Box>
);

const SectionCard = ({ icon, title, children }) => (
  <Box
    sx={{
      bgcolor: "#fafbfc",
      borderRadius: 2.5,
      border: "1px solid #e2e8f0",
      p: 2.5,
      height: "100%",
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          bgcolor: accentLight,
          color: accent,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
        {title}
      </Typography>
    </Box>
    {children}
  </Box>
);

const StatCard = ({ icon, label, value, sub }) => (
  <Box
    sx={{
      bgcolor: "white",
      borderRadius: 3,
      border: "1px solid rgba(0,0,0,0.06)",
      p: 2.5,
      height: "100%",
      display: "flex",
      alignItems: "flex-start",
      gap: 2,
      transition: "box-shadow 0.2s ease",
      "&:hover": { boxShadow: "0 4px 20px rgba(13, 71, 161, 0.08)" },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: 2,
        background: accentGradient,
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography
        variant="h6"
        sx={{ fontWeight: 800, color: "#1a1a2e", lineHeight: 1.2, mt: 0.25 }}
      >
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ color: "#94a3b8", display: "block", mt: 0.5 }}>
          {sub}
        </Typography>
      )}
    </Box>
  </Box>
);

const PaymentCard = ({ payment, index, expanded, onToggle, onCopy }) => {
  const statusStyle = getStatusStyle(payment.status);
  const rz = payment.razorpayDetails || {};
  const hasRazorpay =
    rz.subscriptionId ||
    rz.paymentId ||
    rz.invoiceId ||
    rz.orderId ||
    payment.invoiceNumber ||
    payment.receipt ||
    payment.paymentMethod ||
    payment.mandateId;

  return (
    <Box
      sx={{
        bgcolor: "white",
        borderRadius: 3,
        border: "1px solid rgba(0,0,0,0.06)",
        overflow: "hidden",
        transition: "box-shadow 0.25s ease, transform 0.25s ease",
        "&:hover": {
          boxShadow: "0 12px 40px rgba(13, 71, 161, 0.1)",
        },
      }}
    >
      <Box
        sx={{
          height: 4,
          background:
            payment.status && SUCCESS_STATUSES.has(payment.status)
              ? "linear-gradient(90deg, #15803d, #22c55e)"
              : payment.status === "failed" || payment.status === "cancelled"
                ? "linear-gradient(90deg, #b91c1c, #ef4444)"
                : accentGradient,
        }}
      />

      <Box
        sx={{
          px: { xs: 2, md: 3 },
          py: 2.5,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          cursor: "pointer",
        }}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: accentLight,
              color: accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 800,
              fontSize: "0.95rem",
              flexShrink: 0,
            }}
          >
            {String(index + 1).padStart(2, "0")}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
              {payment.planDetails?.planName || `Payment ${index + 1}`}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
              <Chip
                label={payment.type || "Standard"}
                size="small"
                sx={{
                  height: 24,
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  bgcolor: payment.type === "Main" ? accentLight : "#f3e8ff",
                  color: payment.type === "Main" ? accent : "#7c3aed",
                }}
              />
              <Chip
                label={payment.refund?.label || payment.status}
                size="small"
                sx={{
                  height: 24,
                  fontWeight: 600,
                  fontSize: "0.7rem",
                  bgcolor: payment.refund?.isMandateAuthRefund ? "#ecfdf5" : statusStyle.bg,
                  color: payment.refund?.isMandateAuthRefund ? "#047857" : statusStyle.color,
                  border: `1px solid ${payment.refund?.isMandateAuthRefund ? "#86efac" : statusStyle.border}`,
                }}
              />
              {payment.refund?.refundId ? (
                <Chip
                  label="Refund recorded"
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: "0.7rem",
                    bgcolor: "#fff7ed",
                    color: "#c2410c",
                    fontWeight: 600,
                  }}
                />
              ) : null}
              {payment.planDetails?.autopay && (
                <Chip
                  label="Autopay"
                  size="small"
                  sx={{
                    height: 24,
                    fontSize: "0.7rem",
                    bgcolor: "#ecfdf5",
                    color: "#047857",
                    fontWeight: 600,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: accent, lineHeight: 1.1 }}>
              {formatCurrency(
                payment.amount?.finalPrice,
                payment.amount?.currency
              )}
            </Typography>
            {payment.amount?.discount > 0 && (
              <Typography
                variant="caption"
                sx={{ color: "#94a3b8", textDecoration: "line-through" }}
              >
                {formatCurrency(
                  payment.amount?.originalPrice,
                  payment.amount?.currency
                )}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            sx={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.25s ease",
              color: "#64748b",
            }}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            <ExpandMoreRoundedIcon />
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ px: { xs: 2, md: 3 }, pb: 3, pt: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <SectionCard
                icon={<WorkspacePremiumOutlinedIcon sx={{ fontSize: 18 }} />}
                title="Plan"
              >
                <DetailRow
                  label="Plan name"
                  value={payment.planDetails?.planName}
                />
                <DetailRow
                  label="Validity"
                  value={
                    payment.planDetails?.planValidity
                      ? `${payment.planDetails.planValidity} days`
                      : null
                  }
                />
                <DetailRow
                  label="Autopay"
                  value={payment.planDetails?.autopay ? "Enabled" : "Disabled"}
                />
              </SectionCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <SectionCard
                icon={<CalendarMonthOutlinedIcon sx={{ fontSize: 18 }} />}
                title="Billing"
              >
                <DetailRow
                  label="Payment date"
                  value={formatDate(payment.billingDates?.paymentDate)}
                />
                <DetailRow
                  label="Period start"
                  value={formatDate(payment.billingDates?.billingStart)}
                />
                <DetailRow
                  label="Period end"
                  value={formatDate(payment.billingDates?.billingEnd)}
                />
                {payment.billingDates?.nextPaymentDate && (
                  <DetailRow
                    label="Next payment"
                    value={formatDate(payment.billingDates.nextPaymentDate)}
                  />
                )}
              </SectionCard>
            </Grid>

            {payment.refund?.status ? (
              <Grid item xs={12} md={4}>
                <SectionCard
                  icon={<PaymentsOutlinedIcon sx={{ fontSize: 18 }} />}
                  title="Refund"
                >
                  <DetailRow label="Status" value={payment.refund.label || payment.refund.status} />
                  <DetailRow
                    label="Amount"
                    value={
                      payment.refund.amount != null
                        ? formatCurrency(payment.refund.amount, payment.amount?.currency)
                        : null
                    }
                  />
                  <DetailRow
                    label="Refunded at"
                    value={formatDate(payment.refund.refundedAt)}
                  />
                  {payment.refund.refundId ? (
                    <DetailRow
                      label="Refund ID"
                      value={payment.refund.refundId}
                      onCopy={onCopy}
                    />
                  ) : null}
                </SectionCard>
              </Grid>
            ) : null}

            <Grid item xs={12} md={4}>
              {hasRazorpay ? (
                <SectionCard
                  icon={<CreditCardOutlinedIcon sx={{ fontSize: 18 }} />}
                  title="Transaction IDs"
                >
                  {rz.subscriptionId && (
                    <DetailRow
                      label="Subscription"
                      value={rz.subscriptionId}
                      onCopy={onCopy}
                    />
                  )}
                  {rz.paymentId && (
                    <DetailRow label="Payment" value={rz.paymentId} onCopy={onCopy} />
                  )}
                  {rz.invoiceId && (
                    <DetailRow label="Invoice" value={rz.invoiceId} onCopy={onCopy} />
                  )}
                  {rz.orderId && (
                    <DetailRow label="Order" value={rz.orderId} onCopy={onCopy} />
                  )}
                  {payment.invoiceNumber && (
                    <DetailRow
                      label="Invoice #"
                      value={payment.invoiceNumber}
                      onCopy={onCopy}
                    />
                  )}
                  {payment.receipt && (
                    <DetailRow label="Receipt" value={payment.receipt} onCopy={onCopy} />
                  )}
                  {payment.paymentMethod && (
                    <DetailRow label="Method" value={payment.paymentMethod} />
                  )}
                  {payment.mandateId && (
                    <DetailRow label="Mandate" value={payment.mandateId} onCopy={onCopy} />
                  )}
                </SectionCard>
              ) : (
                <SectionCard
                  icon={<CreditCardOutlinedIcon sx={{ fontSize: 18 }} />}
                  title="Transaction"
                >
                  <Typography variant="body2" sx={{ color: "#94a3b8" }}>
                    No gateway IDs recorded for this payment.
                  </Typography>
                </SectionCard>
              )}
            </Grid>

            {payment.description && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderRadius: 2.5,
                    bgcolor: "#f8fafc",
                    border: "1px dashed #e2e8f0",
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#94a3b8",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    Description
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#475569", mt: 0.5 }}>
                    {payment.description}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>

          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              gap: 1,
              mt: 2.5,
              pt: 2,
              borderTop: "1px solid #f1f5f9",
            }}
          >
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              Created {formatDate(payment.createdAt)}
            </Typography>
            {payment.updatedAt && payment.updatedAt !== payment.createdAt && (
              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                Updated {formatDate(payment.updatedAt)}
              </Typography>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
};

const LoadingSkeleton = () => (
  <Box
    sx={{
      minHeight: "100vh",
      bgcolor: "#fafbfc",
      py: { xs: 2, md: 4 },
      px: { xs: 2, md: 4 },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
      <Skeleton variant="circular" width={40} height={40} />
      <Box sx={{ flex: 1 }}>
        <Skeleton variant="text" width={280} height={36} />
        <Skeleton variant="text" width={200} height={24} />
      </Box>
    </Box>
    <Grid container spacing={2} sx={{ mb: 4 }}>
      {[1, 2, 3].map((i) => (
        <Grid item xs={12} sm={4} key={i}>
          <Skeleton variant="rounded" height={100} sx={{ borderRadius: 3 }} />
        </Grid>
      ))}
    </Grid>
    {[1, 2].map((i) => (
      <Skeleton
        key={i}
        variant="rounded"
        height={120}
        sx={{ borderRadius: 3, mb: 2 }}
      />
    ))}
  </Box>
);

const Paymentdetails2 = () => {
  const { email } = useParams();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userInfo, setUserInfo] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const navigate = useNavigate();

  const fetchPaymentDetails = useCallback(async () => {
    try {
      setLoading(true);
      setAccessDenied(false);
      const decodedEmail = decodeEmail(email);
      const response = await axios.get(`/get-user-payments/${decodedEmail}`, {
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      });

      if (response.data.success && response.data.data.length > 0) {
        setPayments(response.data.data);
        setUserInfo(response.data.data[0].user);
        const firstId = response.data.data[0].id ?? 0;
        setExpandedId(firstId);
      } else {
        setPayments([]);
        setUserInfo(null);
      }
    } catch (error) {
      if (error.response?.status === 403 || error.isAccessDenied) {
        setAccessDenied(true);
      }
      setPayments([]);
      setUserInfo(null);
    } finally {
      setLoading(false);
    }
  }, [email]);

  useEffect(() => {
    if (email) fetchPaymentDetails();
  }, [email, fetchPaymentDetails]);

  const summary = useMemo(() => {
    const totalAmount = payments.reduce(
      (sum, p) => sum + (Number(p.amount?.finalPrice) || 0),
      0
    );
    const currency = payments[0]?.amount?.currency || "INR";
    const successful = payments.filter((p) =>
      SUCCESS_STATUSES.has(p.status)
    ).length;
    return { totalAmount, currency, successful };
  }, [payments]);

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({ open: true, message: "Copied to clipboard" });
    } catch {
      setSnackbar({ open: true, message: "Could not copy" });
    }
  };

  const displayEmail = userInfo?.email || decodeEmail(email);

  if (accessDenied) {
    return <AccessDenied message="You are not allowed to view payments for this user." />;
  }

  if (loading) return <LoadingSkeleton />;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#fafbfc",
        py: { xs: 2, md: 4 },
        px: { xs: 2, sm: 3, md: 4 },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          animation: `${fadeInUp} 0.45s ease-out`,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-start",
            gap: 2,
            mb: 3,
          }}
        >
          <IconButton
            onClick={() => navigate(-1)}
            size="medium"
            sx={{
              bgcolor: "white",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              "&:hover": {
                bgcolor: "#f5f5f5",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1, minWidth: 0 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                background: accentGradient,
                fontWeight: 700,
                fontSize: "1.1rem",
              }}
            >
              {getInitials(userInfo?.name, displayEmail)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: "#1a1a2e",
                  fontSize: { xs: "1.2rem", md: "1.5rem" },
                }}
              >
                {userInfo?.name || "Payment history"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748b",
                  mt: 0.25,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayEmail}
              </Typography>
            </Box>
          </Box>

          <Chip
            icon={<ReceiptLongOutlinedIcon sx={{ fontSize: "18px !important" }} />}
            label={`${payments.length} transaction${payments.length !== 1 ? "s" : ""}`}
            sx={{
              fontWeight: 600,
              bgcolor: accentLight,
              color: accent,
              border: "none",
              height: 36,
            }}
          />
        </Box>

        {payments.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={<PaymentsOutlinedIcon />}
                label="Total transactions"
                value={payments.length}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={<AccountBalanceWalletOutlinedIcon />}
                label="Total billed"
                value={formatCurrency(summary.totalAmount, summary.currency)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <StatCard
                icon={<CheckCircleOutlineRoundedIcon />}
                label="Successful"
                value={summary.successful}
                sub={
                  payments.length > 0
                    ? `${Math.round((summary.successful / payments.length) * 100)}% success rate`
                    : undefined
                }
              />
            </Grid>
          </Grid>
        )}

        {payments.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 10,
              px: 3,
              bgcolor: "white",
              borderRadius: 3,
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                bgcolor: "#f1f5f9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <ReceiptLongOutlinedIcon sx={{ fontSize: 36, color: "#cbd5e1" }} />
            </Box>
            <Typography variant="h6" sx={{ color: "#475569", fontWeight: 600 }}>
              No payments yet
            </Typography>
            <Typography variant="body2" sx={{ color: "#94a3b8", mt: 1, maxWidth: 360, mx: "auto" }}>
              This user has no recorded payment transactions. They will appear here once a
              subscription or purchase is completed.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {payments.map((payment, index) => {
              const id = payment.id ?? index;
              return (
                <PaymentCard
                  key={id}
                  payment={payment}
                  index={index}
                  expanded={expandedId === id}
                  onToggle={() =>
                    setExpandedId((prev) => (prev === id ? null : id))
                  }
                  onCopy={handleCopy}
                />
              );
            })}
          </Box>
        )}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Paymentdetails2;
